import { useCallback, useEffect, useRef } from 'react';

import { InviteLeaseDetailsSegmentEvents } from '../constants';
import type { InviteSession } from '../invite-session.context';
import type { CloseReason, InviteFlowMilestoneKey } from '../types';
import { shouldEmitFromUrl } from '../utils';
import { useInviteTracking } from './use-invite-tracking';

const createEmptyMilestones = (): Record<InviteFlowMilestoneKey, boolean> => ({
  hadFlowStarted: false,
  hadPropertySelected: false,
  hadLabelSelected: false,
  hadSubmitAttempt: false,
  hadSubmitSuccess: false,
});

export const useInviteDrawerSessionTracking = (isOpen: boolean, session: InviteSession | null) => {
  const { trackInvite, entryPoint, flowId } = useInviteTracking(session);
  const fromUrl = session?.fromUrl ?? null;

  const milestonesRef = useRef<Record<InviteFlowMilestoneKey, boolean>>(createEmptyMilestones());
  const trackedFlowIdRef = useRef<string | null>(null);

  // Every opening starts a new session (flow id), including the first one, which also mounts the
  // drawer. Track each once.
  useEffect(() => {
    if (!isOpen || !flowId || trackedFlowIdRef.current === flowId) {
      return;
    }
    trackedFlowIdRef.current = flowId;
    milestonesRef.current = createEmptyMilestones();
    const includeFromUrl = shouldEmitFromUrl(entryPoint, fromUrl);
    trackInvite(InviteLeaseDetailsSegmentEvents.DrawerOpened, {
      entry_point: entryPoint,
      ...(includeFromUrl ? { from_url: fromUrl } : {}),
    });
  }, [isOpen, flowId, trackInvite, entryPoint, fromUrl]);

  const recordInviteFlowMilestone = useCallback((key: InviteFlowMilestoneKey) => {
    milestonesRef.current[key] = true;
  }, []);

  // Must fire exactly once per session; caller owns the guard (see RenterInvite.drawer.tsx).
  const trackDrawerClosed = useCallback(
    (reason: CloseReason) => {
      trackInvite(InviteLeaseDetailsSegmentEvents.DrawerClosed, {
        close_reason: reason,
        had_flow_started: milestonesRef.current.hadFlowStarted,
        had_property_selected: milestonesRef.current.hadPropertySelected,
        had_label_selected: milestonesRef.current.hadLabelSelected,
        had_submit_attempt: milestonesRef.current.hadSubmitAttempt,
        had_submit_success: milestonesRef.current.hadSubmitSuccess,
      });
    },
    [trackInvite],
  );

  return { recordInviteFlowMilestone, trackDrawerClosed };
};
